import { gql, useMutation, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { Account } from '@interfaces/account';
import { AccountStates } from '@interfaces/account';
import type { WorkflowCompletion } from '@interfaces/workflow';
import { Loop } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import type { WorkflowCompletionWithId } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionWithId.types';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import type { CreatedAccountsType } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GO_TO_STEP } from '../queries';
import {
  type ReviewAccountSubstepOptions,
  ReviewAccountVisual,
} from './reviewAccount.visual';

export const WORKFLOW_COMPLETION = `
  workflowCompletion {
    id
    objectId
    context
    workflow {
      name { ${TRANSLATED_STRING_FIELDS} }
      steps {
        id
        name { ${TRANSLATED_STRING_FIELDS} }
        subSteps {
          id
          type
        }
      }
    }
    currentStep {
      id
      name { ${TRANSLATED_STRING_FIELDS} }
      subSteps {
        id
        type
      }
    }
    currentSubStep {
      id
      options
      type
      canGoBack
      skippable
      rolesCompleteableBy {
        id
      }
    }
  }
`;

const FETCH_ACCOUNTS = gql`
  query fetchAccounts($input: FetchAccountsInput!) {
    fetchAccounts(input: $input) {
      accounts {
        id: stringId
        type
        applyForGovFunds
        affiliations {
          id
          allocation
          relation
          type
          user {
            id: stringId
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const ReviewAccount = ({
  options,
  onNext,
  setActiveWorkflowCompletion,
  workflowCompletion,
  userId,
}: {
  options: ReviewAccountSubstepOptions;
  onNext: any;
  setActiveWorkflowCompletion: any;
  workflowCompletion: WorkflowCompletion;
  userId: string;
}) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [accounts, setAccounts] = useState<Account[]>();
  const { t } = useTranslation([
    'workflowCompletions',
    'accountsDetail',
    'affiliationTypes',
    'accountTypes',
  ]);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const createdAccounts = workflowData.createdAccounts ?? [];
  const currentUserId = workflowData.currentUserId || userId;
  const { data, loading: loadingAccounts } = useQuery(FETCH_ACCOUNTS, {
    variables: {
      input: {
        filter: {
          userId: currentUserId,
          states: [AccountStates.INITIATED],
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip:
      !workflowData.currentAccountId ||
      (workflowData.createdAccounts ?? []).length === 0,
    fetchPolicy: 'no-cache',
  });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(loadingAccounts);
  }, [loadingAccounts, setLoading]);

  const { permissions } = usePermissions();

  const [goToStep] = useMutation(GO_TO_STEP(permissions));

  useEffect(() => {
    if (data) {
      setAccounts(data.fetchAccounts.accounts);
    }
  }, [data]);
  const goToAccountCreate = async () => {
    setLoading(true);
    const subSteps = (workflowCompletion?.workflow?.steps || []).flatMap(
      (x: any) => x.subSteps,
    );
    const accountForGoalCreateSubStep = subSteps.find(
      (x: any) => x.type === 'CREATE_ACCOUNT_FOR_GOAL',
    );
    const accountCreateSubStep = subSteps.find(
      (x: any) => x.type === 'CREATE_ACCOUNT',
    );
    const selectedSubStep = accountForGoalCreateSubStep ?? accountCreateSubStep;
    await setWorkflowData({
      ...workflowData,
      currentGoalId: accountCreateSubStep
        ? undefined
        : workflowData.currentGoalId,
      currentAccountId: undefined,
      currentAccountType: undefined,
      currentAccountState: undefined,
      currentSubAccountId: undefined,
    });
    const { data: goToStepData } = await goToStep({
      variables: {
        input: {
          workflowCompletionId: workflowCompletion.id,
          subStepId: selectedSubStep.id,
        },
      },
    });
    setActiveWorkflowCompletion(goToStepData.goToSubStep.workflowCompletion);
    setLoading(false);
  };
  const onSubmit = async () => {
    sendAnalytic(ovAnalyticsEvents.workflowsReviewAccountContinueButtonSelect, {
      workflowStepTitle: options?.title,
      workflowId: workflowCompletion?.workflow?.id,
      workflowName: workflowCompletion?.workflow?.name,
      activeWorkflowCompletionId: workflowCompletion?.id,
      objectId: workflowCompletion?.objectId,
      objectType: workflowCompletion?.objectType,
    });
    await onNext();
  };

  const { activeOrganization } = useContext(UserContext);

  return (
    <WorkflowCompletionForm
      onSubmit={onSubmit}
      buttonFooter={
        options.createAnotherAccount && (
          <Box display='flex' justifyContent='flex-start' flex={1}>
            <Button
              dataTestId='review-account-create-another-button'
              label={t('createAnotherAccount')}
              variant='filled'
              sx={{ mt: 3, mr: 1 }}
              disabled={loading}
              onClick={goToAccountCreate}
              leadingIcon={Loop}
            />
          </Box>
        )
      }
      submitButtonProps={{
        label: t('continue'),
        disabled: loading,
        dataTestId: 'review-account-continue-button',
      }}
    >
      {activeOrganization.availableFeatureFlags?.includes(
        'ALL_ACCOUNT_EDIT_IN_REVIEW',
      ) ? (
        <ReviewAccountVisual
          options={options}
          userId={userId}
          workflowCompletion={workflowCompletion as WorkflowCompletionWithId}
        />
      ) : (
        <>
          <Typography variant='displayLarge' sx={{ mt: 1 }}>
            {translateBackend(options?.title)}
          </Typography>
          <Typography variant='bodyLarge' sx={{ mb: 3 }}>
            {translateBackend(options?.subtitle)}
          </Typography>
          {loading || !workflowData.currentAccountId ? (
            <>
              <Box sx={{ mb: 2 }} display='flex' justifyContent='space-between'>
                <Skeleton width={250} />
                <Skeleton width={150} />
              </Box>
              <Box sx={{ mb: 2 }} display='flex' justifyContent='space-between'>
                <Skeleton width={250} />
                <Skeleton width={150} />
              </Box>
              <Box sx={{ mb: 2 }} display='flex' justifyContent='space-between'>
                <Skeleton width={250} />
                <Skeleton width={150} />
              </Box>
            </>
          ) : (
            <>
              {accounts &&
                accounts.length > 0 &&
                accounts
                  .filter((account: Account) =>
                    createdAccounts?.some(
                      (createdAccount: CreatedAccountsType) =>
                        createdAccount.id === account.id,
                    ),
                  )
                  .map((account: Account) => (
                    <>
                      <Box
                        sx={{ mb: 2 }}
                        display='flex'
                        justifyContent='space-between'
                      >
                        <Typography variant='bodyLarge'>
                          {t('reviewAccount.type')}
                        </Typography>
                        <Typography
                          variant='bodyLarge'
                          sx={{ fontWeight: 'bold', textAlign: 'right' }}
                        >
                          {t(`accountTypes:${account?.type}`)}
                        </Typography>
                      </Box>

                      {account?.applyForGovFunds?.length > 0 &&
                        account.applyForGovFunds.map(
                          (govFund: any, index: number) => (
                            <Box
                              sx={{ mb: 2 }}
                              display='flex'
                              justifyContent='space-between'
                              key={index}
                            >
                              <Typography variant='bodyLarge'>
                                {t(`accountsDetail:govFundOptions.${govFund}`)}
                              </Typography>
                              <Typography
                                variant='bodyLarge'
                                sx={{ fontWeight: 'bold', textAlign: 'right' }}
                              >
                                {t('applied')}
                              </Typography>
                            </Box>
                          ),
                        )}

                      {account &&
                        account?.affiliations?.map((affiliation: any) => (
                          <Box
                            sx={{ mb: 2 }}
                            display='flex'
                            justifyContent='space-between'
                            key={affiliation.id}
                          >
                            <Typography variant='bodyLarge'>
                              {t(
                                `affiliationTypes:${
                                  affiliation.type === 'OTHER'
                                    ? affiliation.relation
                                    : affiliation.type
                                }`,
                              )}
                            </Typography>
                            <Typography
                              variant='bodyLarge'
                              sx={{ fontWeight: 'bold', textAlign: 'right' }}
                            >
                              {affiliation.user.firstName}{' '}
                              {affiliation.user.lastName}
                            </Typography>
                          </Box>
                        ))}
                    </>
                  ))}
            </>
          )}
        </>
      )}
    </WorkflowCompletionForm>
  );
};

export default ReviewAccount;
